import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { Link } from 'react-router';
import { translate } from 'react-i18next';
import { isMobile, isBrowser } from 'react-device-detect';
import ProgressiveImage from 'react-progressive-image';

import Navigation from 'components/molecules/Navigation';
import Hamburger from 'components/atoms/Hamburger';
import NavigationSide from 'components/organisms/NavigationSide';
import NavExtraItem from 'components/molecules/NavExtraItem';
import NavSustainabilityItem from 'components/molecules/NavSustainabilityItem';

import { lanMapRoute } from 'util/i18n';
import { hotelPages } from 'util/navi';
import { TAIWAN_HOTEL_LIST } from 'util/constants';

import styles from './index.css';

const BookBtn = ({ className, onClick, children }) => (
	<span
		className={classnames(styles.bookBtn, className, {
			[styles.mobileDevice]: isMobile,
			[styles.isBrowser]: isBrowser,
		})}
		role="button"
		tabIndex="0"
		onClick={onClick}
		onKeyPress={() => {}}
	>
		{children}
	</span>
);

const HeaderHotel = ({
	// translate
	t,

	// redux data
	hotel,
	lan,
	media,
	page,
	bannerImage,
	bannerThumbnail,
	bannerText,

	// states
	isSideNaviOpened,
	fixNav,

	// actions
	toggleNavi,
	closeNavi,
	onClickOutside,
	getImageHeight,
	bookHotel,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);
	const isTraditionalChinese = lan === 'zh-TW';
	const isTaiwanHotel = TAIWAN_HOTEL_LIST.includes(hotel);

	useEffect(() => {
		const handleClickOutside = event => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	const onClick = () => {
		if (isTraditionalChinese && isTaiwanHotel) {
			setIsOpen(!isOpen);
		} else {
			bookHotel();
		}
	};

	const onClickHref = () => {
		window.open('https://twanga.mohist.com.tw/LIKEGO/index.php?GetStore=SLK', '_blank');
	};

	return (
		<header className={styles.headerHotel}>
			{/* 漢堡打開的 menu */}
			<div
				className={classnames(styles.naviSide, {
					[styles.mediaNormal]: lan !== 'en',
					[styles.mediaEn]: lan === 'en',
					[styles.active]: isSideNaviOpened,
					[styles.home]: page === '',
					[styles.headerFix]: fixNav,
				})}
			>
				<NavigationSide
					type="hotel"
					closeNavi={closeNavi}
					onClickOutside={onClickOutside}
					extraComponent={media === 'phone' && BookBtn}
					extraComponentProps={{ t, bookHotel, className: styles.naviSideBookBtn }}
				/>
			</div>
			<nav
				className={classnames({
					[styles.mediaNormal]: lan !== 'en',
					[styles.mediaEn]: lan === 'en',
					[styles.home]: page === '',
					[styles.fix]: fixNav,
				})}
			>
				<div
					className={classnames(styles.hamburger, {
						[styles.mediaNormal]: lan !== 'en',
						[styles.mediaEn]: lan === 'en',
					})}
					role="button"
					tabIndex="0"
					onClick={toggleNavi}
					onKeyPress={() => {}}
				>
					<Hamburger open={isSideNaviOpened} />
				</div>
				<Navigation
					className={classnames(styles.navigation, {
						[styles.mediaNormal]: lan !== 'en',
						[styles.mediaEn]: lan === 'en',
					})}
					type="hotel"
				/>
				<div
					className={classnames(styles.pageGuide, {
						[styles.mediaNormal]: lan !== 'en',
						[styles.mediaEn]: lan === 'en',
					})}
				>
					{hotelPages.map(hotelPage => (
						<Link key={hotelPage.route} to={`/${hotel}/${lanMapRoute(lan)}/${hotelPage.route}`}>
							<span
								className={classnames(styles.navItem, styles[lan], {
									[styles.mediaNormal]: lan !== 'en',
									[styles.mediaEn]: lan === 'en',
									[styles.active]: page === hotelPage.route,
									[styles.isBrowser]: isBrowser,
								})}
							>
								{t(hotelPage.label)}
							</span>
						</Link>
					))}
					<NavExtraItem
						className={classnames(styles.navItem, {
							[styles.mediaNormal]: lan !== 'en',
							[styles.mediaEn]: lan === 'en',
							[styles.isBrowser]: isBrowser,
						})}
						hotel={hotel}
						lan={lan}
					/>
					<NavSustainabilityItem
						className={classnames(styles.navItem, {
							[styles.mediaNormal]: lan !== 'en',
							[styles.mediaEn]: lan === 'en',
							[styles.isBrowser]: isBrowser,
						})}
						hotel={hotel}
						lan={lan}
					/>
				</div>
				<div ref={dropdownRef}>
					<BookBtn
						className={classnames(styles.bookBtn, styles[lan], {
							[styles.mediaNormal]: lan !== 'en',
							[styles.mediaEn]: lan === 'en',
						})}
						onClick={onClick}
					>
						{t('bookNowBtn')}
					</BookBtn>
					{isOpen && (
						<div className={styles.fixedPosition}>
							<BookBtn
								className={classnames(styles.bookBtn, styles.bookBtnItem, styles[lan], {
									[styles.mediaNormal]: lan !== 'en',
									[styles.mediaEn]: lan === 'en',
								})}
								onClick={() => {
									bookHotel();
									setIsOpen(false);
								}}
							>
								{t('bookNow')}
							</BookBtn>
							<BookBtn
								className={classnames(styles.bookBtn, styles.bookBtnItem, styles[lan], {
									[styles.mediaNormal]: lan !== 'en',
									[styles.mediaEn]: lan === 'en',
								})}
								onClick={() => {
									onClickHref();
									setIsOpen(false);
								}}
							>
								{t('onlineTravelFair')}
							</BookBtn>
						</div>
					)}
				</div>
			</nav>
			{page === '' && (
				<ProgressiveImage src={bannerImage} placeholder="">
					{(src, loading) => (
						<div
							className={classnames(styles.banner, {
								[styles.mediaNormal]: lan !== 'en',
								[styles.mediaEn]: lan === 'en',
							})}
							style={{
								backgroundImage: `url(${loading ? bannerThumbnail : src})`,
								opacity: loading ? 0.5 : 1,
							}}
							ref={node => {
								if (node) {
									getImageHeight(node.getBoundingClientRect().height);
								}
							}}
						>
							<div
								className={classnames(styles.container, {
									[styles.mediaNormal]: lan !== 'en',
									[styles.mediaEn]: lan === 'en',
								})}
							>
								{bannerText}
							</div>
						</div>
					)}
				</ProgressiveImage>
			)}
		</header>
	);
};

export default translate('header')(HeaderHotel);



// WEBPACK FOOTER //
// ./src/components/organisms/HeaderHotel/component.js